import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Card,
    Button,
    Typography,
    IconButton,
    Alert,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    CircularProgress,
    LinearProgress,
    Divider,
    TextField,
    MenuItem,
    Autocomplete,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormLabel,
    Select,
    Tooltip,
} from "@mui/material";
import { ArrowBack, Download, UploadFile, CheckCircle, Add, DeleteOutlineOutlined, Storefront, Description, Person, InfoOutlined } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import shipmentService from "../../services/shipmentService";
import documentService from "../../services/documentService";
import userService from "../../services/userService";
import { selectCurrencyOptions } from "../../redux/slices/currenciesSlice";
import CurrencyFlag from "../../components/common/CurrencyFlag";
import { getCurrencyCountry } from "../../utils/currencyCountries";

const STEPS = { FORM: "form", REVIEW: "review", IMPORTING: "importing", RESULT: "result" };

// Same numbered-section look as the single-shipment form (ShipmentForm)
// and its own local copy in CustomerBulkAddShipments.jsx — kept as its own
// copy here too, same reasoning (tiny, purely presentational).
const SectionHeader = ({ icon, number, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}>
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: "info.light",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: "primary.main",
            }}
        >
            {icon}
        </Box>
        <Box>
            <Typography fontWeight={700} fontSize={14}>
                {number}. {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {subtitle}
            </Typography>
        </Box>
    </Box>
);

// Same options as the single-shipment create form (ShipmentForm) —
// kept in sync by hand, since Country/Carrier are fixed, admin-independent
// lists there too, not admin-configurable statuses.
const COUNTRY_OPTIONS = ["USA", "UK", "Canada", "China", "Other"];
const CARRIER_OPTIONS = [
    { value: "", label: "Select carrier…" },
    { value: "UPS", label: "UPS" },
    { value: "FEDEX", label: "FedEx" },
    { value: "USPS", label: "USPS" },
    { value: "DHL", label: "DHL" },
    { value: "AMAZON", label: "Amazon Logistics" },
    { value: "OTHER", label: "Other" },
];
const VENDOR_NAME_SUGGESTIONS = [
    "Amazon", "eBay", "SHEIN", "Fashion Nova", "Walmart", "Target", "Temu",
    "AliExpress", "Nike", "Adidas", "Zara", "H&M", "Home Depot", "Lowe's",
    "Best Buy", "Apple", "Etsy", "Costco",
];

let nextId = 0;
const uid = () => `${Date.now()}-${nextId++}`;

const emptyItem = () => ({ _id: uid(), description: "", quantity: "1", trackingNumber: "" });
const emptyOrder = () => ({
    _id: uid(),
    orderNumber: "",
    packageDescription: "",
    invoiceTotal: "",
    invoiceCurrency: "USD",
    docType: "invoice",
    invoiceFiles: [],
    items: [emptyItem()],
});

// Purchase Invoice upload — same dropzone look/behavior as
// CustomerBulkAddShipments.jsx's own copy.
const OrderInvoiceUpload = ({ files, onChange }) => {
    const onDrop = useCallback(
        (accepted) => {
            onChange([...(files || []), ...accepted.map((f) => Object.assign(f, { preview: URL.createObjectURL(f) }))]);
        },
        [files, onChange],
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            "application/pdf": [".pdf"],
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"],
            "image/webp": [".webp"],
        },
        maxSize: 10 * 1024 * 1024,
    });
    const removeFile = (idx) => onChange((files || []).filter((_, i) => i !== idx));

    return (
        <Box>
            <Box
                {...getRootProps()}
                sx={{
                    border: "1.5px dashed",
                    borderColor: isDragActive ? "primary.main" : "grey.400",
                    borderRadius: 1.5,
                    py: 1.5,
                    px: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: 0.25,
                    cursor: "pointer",
                    bgcolor: isDragActive ? "rgba(13,98,155,0.04)" : "action.hover",
                }}
            >
                <input {...getInputProps()} />
                <UploadFile sx={{ fontSize: 20, color: "text.disabled", mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                    {files?.length > 0 ? "Attach another file" : "Attach Purchase Invoice"}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                    Drag &amp; drop or click to browse
                </Typography>
            </Box>

            {files?.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.5 }}>
                    {files.map((file, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                p: 1,
                                border: "1.5px solid",
                                borderColor: "success.main",
                                borderRadius: 1,
                                bgcolor: "success.light",
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                                <CheckCircle sx={{ fontSize: 18, color: "success.main", flexShrink: 0 }} />
                                <Typography noWrap sx={{ mr: 2, flex: 1 }} variant="body2">
                                    {file.name}
                                </Typography>
                            </Box>
                            <Button size="small" color="error" onClick={() => removeFile(idx)}>
                                Remove
                            </Button>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

const csvEscape = (val) => {
    const s = String(val ?? "");
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadReportCsv = (result) => {
    const headers = ["status", "rowNumbers", "shipmentTrackingNumber", "itemCount", "reason"];
    const rows = [
        ...result.created.map((c) => ["created", c.rowNumbers.join("; "), c.trackingNumber, c.itemCount, ""]),
        ...result.failed.map((f) => ["failed", f.rowNumbers.join("; "), "", "", f.errors.join("; ")]),
    ];
    const csv = [headers, ...rows].map((row) => row.map(csvEscape).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bulk-add-report-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Admin counterpart to CustomerBulkAddShipments.jsx — identical manual-entry
// form (Shared Details + repeatable Orders/Items), same underlying
// preview/confirm pipeline, except the admin picks WHICH customer these
// shipments belong to first (a required customer search, same pattern as
// ShipmentForm's own admin customer picker) instead of it always
// being "yourself".
const AdminBulkAddShipments = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const currencyOptions = useSelector(selectCurrencyOptions);

    // ── Customer (admin-only step) ──────────────────────────────────────────
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerInput, setCustomerInput] = useState("");

    useEffect(() => {
        const query = customerInput.trim();
        if (query.length < 2) {
            setCustomerOptions([]);
            return;
        }
        setCustomerLoading(true);
        const timer = setTimeout(() => {
            userService
                .getAll({ role: "customer", search: query, limit: 20 })
                .then(({ data }) => setCustomerOptions(data.data || []))
                .catch(() => setCustomerOptions([]))
                .finally(() => setCustomerLoading(false));
        }, 500);
        return () => clearTimeout(timer);
    }, [customerInput]);

    const showTinPicker = !!selectedCustomer?.canManageMultipleTins && selectedCustomer?.tins?.length > 0;
    const [tin, setTin] = useState("");
    useEffect(() => {
        setTin(showTinPicker ? selectedCustomer.tins[0].tin : "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomer]);

    // ── Shared Details (once per batch) ────────────────────────────────────
    const [vendorName, setVendorName] = useState("");
    const [vendorWebsite, setVendorWebsite] = useState("");
    const [vendorCountry, setVendorCountry] = useState("USA");
    const [carrier, setCarrier] = useState("");
    const [trackingMode, setTrackingMode] = useState("single"); // "single" | "perItem"
    const [sharedTracking, setSharedTracking] = useState("");

    const [vendorSuggestions, setVendorSuggestions] = useState([]);
    useEffect(() => {
        const query = vendorName.trim().toLowerCase();
        if (!query) {
            setVendorSuggestions([]);
            return;
        }
        const timer = setTimeout(() => {
            setVendorSuggestions(VENDOR_NAME_SUGGESTIONS.filter((v) => v.toLowerCase().includes(query)));
        }, 500);
        return () => clearTimeout(timer);
    }, [vendorName]);

    // ── Orders (repeatable, each with nested items) ────────────────────────
    const [orders, setOrders] = useState([emptyOrder()]);

    const [step, setStep] = useState(STEPS.FORM);
    const [submitting, setSubmitting] = useState(false);
    const [groups, setGroups] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState({
        shipmentsDone: 0,
        totalShipments: 0,
        itemsDone: 0,
        totalItems: 0,
    });
    const [result, setResult] = useState(null);
    const [rowIndexToOrderId, setRowIndexToOrderId] = useState([]);
    const [uploadingInvoices, setUploadingInvoices] = useState(false);

    const updateOrder = (orderId, patch) =>
        setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, ...patch } : o)));

    const addOrder = () => setOrders((prev) => [...prev, emptyOrder()]);
    const removeOrder = (orderId) => setOrders((prev) => (prev.length > 1 ? prev.filter((o) => o._id !== orderId) : prev));

    const updateItem = (orderId, itemId, patch) =>
        setOrders((prev) =>
            prev.map((o) =>
                o._id !== orderId
                    ? o
                    : { ...o, items: o.items.map((it) => (it._id === itemId ? { ...it, ...patch } : it)) },
            ),
        );
    const addItem = (orderId) =>
        setOrders((prev) => prev.map((o) => (o._id === orderId ? { ...o, items: [...o.items, emptyItem()] } : o)));
    const removeItem = (orderId, itemId) =>
        setOrders((prev) =>
            prev.map((o) =>
                o._id !== orderId || o.items.length <= 1 ? o : { ...o, items: o.items.filter((it) => it._id !== itemId) },
            ),
        );

    const handleReview = async () => {
        if (!selectedCustomer) return;
        const rows = [];
        const indexToOrderId = [];
        orders.forEach((order, oi) => {
            order.items.forEach((item) => {
                indexToOrderId.push(order._id);
                rows.push({
                    shipmentRef: `order-${oi}`,
                    vendorName,
                    vendorWebsite,
                    vendorCountry,
                    carrier,
                    orderNumber: order.orderNumber,
                    invoiceTotal: order.invoiceTotal,
                    invoiceCurrency: order.invoiceCurrency,
                    packageDescription: order.packageDescription,
                    trackingNumber: trackingMode === "single" ? sharedTracking : item.trackingNumber,
                    itemDescription: item.description,
                    quantity: item.quantity,
                });
            });
        });

        if (!rows.length) return;
        setSubmitting(true);
        try {
            const { data } = await shipmentService.bulkImportPreview(rows, trackingMode === "single", selectedCustomer._id);
            setGroups(data.data.groups);
            setTotalRows(data.data.totalRows);
            setRowIndexToOrderId(indexToOrderId);
            setStep(STEPS.REVIEW);
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to validate these orders", { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    const validGroups = groups.filter((g) => g.errors.length === 0);
    const invalidGroups = groups.filter((g) => g.errors.length > 0);
    const totalItems = groups.reduce((n, g) => n + g.items.length, 0);

    const handleConfirm = async () => {
        if (!validGroups.length || !selectedCustomer) return;
        setImporting(true);
        setImportProgress({ shipmentsDone: 0, totalShipments: validGroups.length, itemsDone: 0, totalItems: 0 });
        setStep(STEPS.IMPORTING);
        try {
            const final = await shipmentService.bulkImportConfirm(
                validGroups,
                (evt) => {
                    setImportProgress({
                        shipmentsDone: evt.shipmentsDone ?? 0,
                        totalShipments: evt.totalShipments ?? 0,
                        itemsDone: evt.itemsDone ?? 0,
                        totalItems: evt.totalItems ?? 0,
                    });
                },
                showTinPicker ? tin : undefined,
                trackingMode === "single",
                selectedCustomer._id,
            );
            setResult(final);
            setStep(STEPS.RESULT);

            // Best-effort: attach each order's Purchase Invoice file(s) (if
            // any were chosen) to its newly-created shipment — same
            // rowNumber-offset recovery as CustomerBulkAddShipments.jsx.
            const uploadsToRun = (final.created || [])
                .map((c) => {
                    const orderId = rowIndexToOrderId[(c.rowNumbers[0] ?? 2) - 2];
                    const order = orders.find((o) => o._id === orderId);
                    return order?.invoiceFiles?.length
                        ? { shipmentId: c.shipmentId, files: order.invoiceFiles, docType: order.docType || "invoice" }
                        : null;
                })
                .filter(Boolean);

            if (uploadsToRun.length) {
                setUploadingInvoices(true);
                await Promise.all(
                    uploadsToRun.map(({ shipmentId, files, docType }) =>
                        documentService.upload(shipmentId, files, docType).catch(() => null),
                    ),
                );
                setUploadingInvoices(false);
            }
        } catch (err) {
            enqueueSnackbar(err.message || "Failed to create shipments", { variant: "error" });
            setStep(STEPS.REVIEW);
        } finally {
            setImporting(false);
        }
    };

    // Mirrors ShipmentForm's single-add canSubmit: every order (each
    // becomes its own shipment) must satisfy the same requirements a single
    // shipment would, and every item on an order needs a description, not
    // just one of them.
    const canReview =
        !!selectedCustomer &&
        orders.length > 0 &&
        !!vendorName.trim() &&
        !!vendorCountry &&
        orders.every(
            (o) =>
                o.orderNumber.trim() &&
                o.packageDescription.trim() &&
                !!o.docType &&
                o.invoiceFiles?.length > 0 &&
                o.invoiceTotal &&
                parseFloat(o.invoiceTotal) > 0 &&
                o.items.length > 0 &&
                o.items.every((it) => it.description.trim()),
        );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/admin/shipments"))}
                    variant="outlined"
                    size="small"
                    disabled={importing}
                >
                    Back
                </Button>
                <Box>
                    <Typography variant="h4" fontWeight={700} color="text.primary">
                        Bulk Add (Manual Entry)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Add several orders at once on a customer's behalf, without a CSV file
                    </Typography>
                </Box>
            </Box>

            <Card sx={{ p: { xs: 1.5, sm: 3 } }}>
                {step === STEPS.FORM && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            Fill in the details once below, then add one block per order. Have a spreadsheet of
                            orders instead? Use <strong>Bulk Import (CSV)</strong> from the Shipments page instead —
                            that's the faster option for a large list.
                        </Alert>

                        {/* ── Customer ─────────────────────────────────────── */}
                        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                            <SectionHeader
                                icon={<Person sx={{ fontSize: 18 }} />}
                                number={1}
                                title="Customer"
                                subtitle="Who these shipments are being created for."
                            />
                            <Autocomplete
                                options={customerOptions}
                                loading={customerLoading}
                                value={selectedCustomer}
                                onChange={(_, val) => setSelectedCustomer(val)}
                                inputValue={customerInput}
                                onInputChange={(_, val) => setCustomerInput(val)}
                                filterOptions={(x) => x}
                                getOptionLabel={(o) => (o ? `${o.firstName} ${o.lastName} (${o.email})` : "")}
                                isOptionEqualToValue={(o, v) => o._id === v._id}
                                noOptionsText={
                                    customerInput.trim().length < 2
                                        ? "Type at least 2 characters…"
                                        : customerLoading
                                          ? "Searching…"
                                          : "No customers found"
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select customer"
                                        placeholder="Search by name, email, or TIN…"
                                        required
                                        slotProps={{
                                            ...params.slotProps,
                                            input: {
                                                ...params.slotProps?.input,
                                                endAdornment: (
                                                    <>
                                                        {customerLoading && <CircularProgress size={16} />}
                                                        {params.slotProps?.input?.endAdornment}
                                                    </>
                                                ),
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>

                        {/* ── Shared Details ──────────────────────────────── */}
                        <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                            <SectionHeader
                                icon={<Storefront sx={{ fontSize: 18 }} />}
                                number={2}
                                title="Shared Details"
                                subtitle="Applies to every order below — vendor, carrier, and (if chosen) tracking."
                            />
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
                                <Autocomplete
                                    freeSolo
                                    options={vendorSuggestions}
                                    value={vendorName}
                                    inputValue={vendorName}
                                    onInputChange={(_, value) => setVendorName(value)}
                                    onChange={(_, value) => setVendorName(value || "")}
                                    filterOptions={(options) => options}
                                    renderInput={(params) => <TextField {...params} label="Store / Vendor Name" required />}
                                />
                                <TextField label="Website (optional)" value={vendorWebsite} onChange={(e) => setVendorWebsite(e.target.value)} fullWidth />
                                <TextField label="Country" select value={vendorCountry} onChange={(e) => setVendorCountry(e.target.value)} required fullWidth>
                                    {COUNTRY_OPTIONS.map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField label="Carrier (optional)" select value={carrier} onChange={(e) => setCarrier(e.target.value)} fullWidth>
                                    {CARRIER_OPTIONS.map((c) => (
                                        <MenuItem key={c.value} value={c.value}>
                                            {c.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            <FormLabel sx={{ fontSize: 13, fontWeight: 600 }}>Tracking Number</FormLabel>
                            <RadioGroup row value={trackingMode} onChange={(e) => setTrackingMode(e.target.value)} sx={{ mb: 1 }}>
                                <FormControlLabel value="single" control={<Radio size="small" />} label="Single tracking for all orders" />
                                <FormControlLabel value="perItem" control={<Radio size="small" />} label="Each item has its own tracking" />
                            </RadioGroup>
                            {trackingMode === "single" && (
                                <TextField label="Tracking Number" value={sharedTracking} onChange={(e) => setSharedTracking(e.target.value)} fullWidth />
                            )}
                        </Box>

                        {/* ── Orders ───────────────────────────────────────── */}
                        <SectionHeader
                            icon={<Description sx={{ fontSize: 18 }} />}
                            number={3}
                            title="Orders"
                            subtitle="One block per order, each with its own item(s) and Purchase Invoice."
                        />
                        {orders.map((order, oi) => (
                            <Box key={order._id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                                    <Typography variant="subtitle2" fontWeight={700}>
                                        Order#: {order.orderNumber.trim() || oi + 1}
                                    </Typography>
                                    {orders.length > 1 && (
                                        <IconButton size="small" onClick={() => removeOrder(order._id)}>
                                            <DeleteOutlineOutlined fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>

                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
                                    <TextField
                                        label="Order Number"
                                        value={order.orderNumber}
                                        onChange={(e) => updateOrder(order._id, { orderNumber: e.target.value })}
                                        required
                                        fullWidth
                                    />
                                    <TextField
                                        label="Package Description"
                                        value={order.packageDescription}
                                        onChange={(e) => updateOrder(order._id, { packageDescription: e.target.value })}
                                        placeholder="e.g. Sony headphones"
                                        required
                                        fullWidth
                                    />
                                    <TextField
                                        label="Total Invoice Amount"
                                        type="number"
                                        value={order.invoiceTotal}
                                        onChange={(e) => updateOrder(order._id, { invoiceTotal: e.target.value })}
                                        required
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <Select
                                                        value={order.invoiceCurrency || "USD"}
                                                        onChange={(e) => updateOrder(order._id, { invoiceCurrency: e.target.value })}
                                                        variant="standard"
                                                        disableUnderline
                                                        renderValue={(c) => (
                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                                <CurrencyFlag code={c} />
                                                                {c}
                                                            </Box>
                                                        )}
                                                        sx={{
                                                            mr: 0.75,
                                                            fontSize: 13,
                                                            fontWeight: 700,
                                                            bgcolor: "action.selected",
                                                            borderRadius: 1.5,
                                                            "& .MuiSelect-select": { py: 0.4, pl: 1, pr: "26px !important" },
                                                            "& .MuiSelect-icon": { color: "text.secondary" },
                                                        }}
                                                    >
                                                        {currencyOptions.map((c) => (
                                                            <MenuItem key={c} value={c} sx={{ fontSize: 13, display: "flex", gap: 1 }}>
                                                                <CurrencyFlag code={c} />
                                                                {c} — {getCurrencyCountry(c)?.countryName || "—"}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                ),
                                                endAdornment: (
                                                    <Tooltip
                                                        title="Total amount shown on your purchase invoice, before any taxes or duties. Pick the currency it's actually billed in — it's converted to USD automatically using that day's live exchange rate when the shipment is created."
                                                        arrow
                                                    >
                                                        <InfoOutlined sx={{ fontSize: 19, color: "primary.main", cursor: "help" }} />
                                                    </Tooltip>
                                                ),
                                            },
                                        }}
                                    />
                                    <TextField
                                        select
                                        label="Doc Type"
                                        value={order.docType}
                                        onChange={(e) => updateOrder(order._id, { docType: e.target.value })}
                                        required
                                        fullWidth
                                    >
                                        <MenuItem value="invoice">Purchase Invoice</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                    </TextField>
                                </Box>

                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                                    Purchase Invoice *
                                </Typography>
                                <OrderInvoiceUpload
                                    files={order.invoiceFiles}
                                    onChange={(files) => updateOrder(order._id, { invoiceFiles: files })}
                                />

                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mt={2} mb={1}>
                                    Items
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    {order.items.map((item) => (
                                        <Box
                                            key={item._id}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: {
                                                    xs: "1fr 1fr 36px",
                                                    md: trackingMode === "perItem" ? "1fr 1fr 100px 130px 36px" : "1fr 100px 130px 36px",
                                                },
                                                gap: 1,
                                                alignItems: { xs: "stretch", md: "flex-start" },
                                                p: 1,
                                                borderRadius: 2,
                                                bgcolor: "action.hover",
                                                border: "1px solid",
                                                borderColor: "divider",
                                            }}
                                        >
                                            <TextField
                                                size="small"
                                                label="Item Description"
                                                value={item.description}
                                                onChange={(e) => updateItem(order._id, item._id, { description: e.target.value })}
                                                required
                                                sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}
                                            />
                                            {trackingMode === "perItem" && (
                                                <TextField
                                                    size="small"
                                                    label="Tracking #"
                                                    value={item.trackingNumber}
                                                    onChange={(e) => updateItem(order._id, item._id, { trackingNumber: e.target.value })}
                                                    sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}
                                                />
                                            )}
                                            <TextField
                                                size="small"
                                                label="Qty"
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => updateItem(order._id, item._id, { quantity: e.target.value })}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => removeItem(order._id, item._id)}
                                                disabled={order.items.length <= 1}
                                                sx={{
                                                    justifySelf: { xs: "end", md: "center" },
                                                    mt: { xs: 0, md: 0.5 },
                                                }}
                                            >
                                                <DeleteOutlineOutlined fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                    <Button size="small" startIcon={<Add />} onClick={() => addItem(order._id)} sx={{ alignSelf: "flex-start" }}>
                                        Add Item
                                    </Button>
                                </Box>
                            </Box>
                        ))}

                        <Button variant="outlined" startIcon={<Add />} onClick={addOrder} sx={{ alignSelf: "flex-start" }}>
                            Add Another Order
                        </Button>

                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button onClick={handleReview} variant="contained" disabled={!canReview || submitting}>
                                {submitting ? <CircularProgress size={18} /> : "Review Orders"}
                            </Button>
                        </Box>
                    </Box>
                )}

                {step === STEPS.REVIEW && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {showTinPicker && (
                            <TextField select label="File under TIN" value={tin} onChange={(e) => setTin(e.target.value)} sx={{ maxWidth: 320 }}>
                                {selectedCustomer.tins.map((t) => (
                                    <MenuItem key={t.tin} value={t.tin}>
                                        {t.label ? `${t.tin} (${t.label})` : t.tin}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                        <Alert severity={invalidGroups.length ? "warning" : "success"} sx={{ borderRadius: 2 }}>
                            {validGroups.length} of {groups.length} shipment(s) ready to create ({totalItems} item(s)
                            across {totalRows} row(s))
                            {invalidGroups.length ? ` — ${invalidGroups.length} need fixing (shown in red)` : ""}.
                        </Alert>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {groups.map((g, gi) => (
                                <Box
                                    key={gi}
                                    sx={{ border: "1px solid", borderColor: g.errors.length ? "error.light" : "divider", borderRadius: 2, overflow: "hidden" }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            gap: 1,
                                            px: 1.5,
                                            py: 1,
                                            bgcolor: g.errors.length ? "rgba(197,48,48,0.06)" : "action.hover",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            <Typography variant="body2" fontWeight={700}>
                                                {g.vendorName || "(vendor missing)"}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {g.items.length} item{g.items.length === 1 ? "" : "s"}
                                                {g.orderNumber ? ` · order ${g.orderNumber}` : ""}
                                                {g.carrier ? ` · ${g.carrier}` : ""}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                ${Number(g.invoiceTotal || 0).toFixed(2)}
                                            </Typography>
                                            {g.errors.length === 0 && <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />}
                                        </Box>
                                    </Box>

                                    {g.errors.length > 0 && (
                                        <Box sx={{ px: 1.5, py: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {g.errors.map((e, i) => (
                                                <Chip key={i} label={e} size="small" color="error" variant="outlined" sx={{ height: "auto", py: 0.4, fontSize: 10.5, "& .MuiChip-label": { whiteSpace: "normal" } }} />
                                            ))}
                                        </Box>
                                    )}

                                    <Box sx={{ overflowX: "auto" }}>
                                        <Table size="small" sx={{ minWidth: 520 }}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Item</TableCell>
                                                    <TableCell align="right">Qty</TableCell>
                                                    <TableCell>Tracking #</TableCell>
                                                    <TableCell>Issues</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {g.items.map((item) => (
                                                    <TableRow key={item.rowNumber} sx={{ bgcolor: item.errors.length ? "rgba(197,48,48,0.04)" : undefined }}>
                                                        <TableCell>{item.description || "—"}</TableCell>
                                                        <TableCell align="right">{item.quantity}</TableCell>
                                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{item.trackingNumber || "—"}</TableCell>
                                                        <TableCell>
                                                            {item.errors.length > 0 && (
                                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                                                    {item.errors.map((e, i) => (
                                                                        <Chip key={i} label={e} size="small" color="error" variant="outlined" sx={{ height: "auto", py: 0.4, fontSize: 10.5, "& .MuiChip-label": { whiteSpace: "normal" } }} />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Button onClick={() => setStep(STEPS.FORM)} variant="outlined">
                                Back
                            </Button>
                            <Button
                                onClick={handleConfirm}
                                variant="contained"
                                disabled={!validGroups.length || (showTinPicker && !tin)}
                            >
                                {`Create ${validGroups.length} Shipment${validGroups.length === 1 ? "" : "s"}`}
                            </Button>
                        </Box>
                    </Box>
                )}

                {step === STEPS.IMPORTING && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 4 }}>
                        <LinearProgress
                            variant={importProgress.totalShipments ? "determinate" : "indeterminate"}
                            value={importProgress.totalShipments ? Math.round((importProgress.shipmentsDone / importProgress.totalShipments) * 100) : 0}
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body1" fontWeight={600} textAlign="center">
                            {importProgress.shipmentsDone} ({importProgress.itemsDone} items) of{" "}
                            {importProgress.totalShipments} ({importProgress.totalItems} items) shipments created
                        </Typography>
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                            Please keep this page open until it finishes.
                        </Typography>
                    </Box>
                )}

                {step === STEPS.RESULT && result && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Alert
                            severity={result.createdCount > 0 ? "success" : "warning"}
                            sx={{ borderRadius: 2 }}
                            action={
                                <Button color="inherit" size="small" startIcon={<Download />} onClick={() => downloadReportCsv(result)}>
                                    Download Report
                                </Button>
                            }
                        >
                            {result.createdCount} shipment(s) created
                            {result.failedCount ? `, ${result.failedCount} shipment(s) failed` : ""}.
                            {uploadingInvoices ? " Attaching invoices…" : ""}
                        </Alert>

                        {result.created.length > 0 && (
                            <Box>
                                <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                                    Created
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {result.created.map((c) => (
                                        <Chip
                                            key={c.shipmentId}
                                            label={`${c.trackingNumber} (${c.itemCount} item${c.itemCount === 1 ? "" : "s"})`}
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            sx={{ fontFamily: "monospace" }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {result.failed.length > 0 && (
                            <>
                                <Divider />
                                <Box>
                                    <Typography variant="overline" color="text.secondary" display="block" mb={0.5}>
                                        Failed
                                    </Typography>
                                    {result.failed.map((f, i) => (
                                        <Typography key={i} variant="body2" color="error.main">
                                            {f.errors.join(", ")}
                                        </Typography>
                                    ))}
                                </Box>
                            </>
                        )}

                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button variant="contained" onClick={() => navigate("/admin/shipments")}>
                                Back to Shipments
                            </Button>
                        </Box>
                    </Box>
                )}
            </Card>
        </Box>
    );
};

export default AdminBulkAddShipments;
